import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function QuestionFormDialog({ open, onOpenChange, courseId, initial, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    text: "",
    options: ["", "", "", ""],
    correct_index: 0,
    explanation: "",
    tags: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const idx = initial.options.findIndex((o) => o.id === initial.correct_option_id);
      setForm({
        text: initial.text,
        options: initial.options.map((o) => o.text),
        correct_index: idx >= 0 ? idx : 0,
        explanation: initial.explanation || "",
        tags: (initial.tags || []).join(", "),
      });
    } else {
      setForm({ text: "", options: ["", "", "", ""], correct_index: 0, explanation: "", tags: "" });
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.options.some((o) => !o.trim())) {
      toast.error("Compila tutte e 4 le opzioni");
      return;
    }
    setSaving(true);
    try {
      const body = {
        text: form.text,
        options: form.options,
        correct_index: form.correct_index,
        explanation: form.explanation || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      let data;
      if (isEdit) {
        const res = await api.patch(`/questions/${initial.id}`, body);
        data = res.data;
        toast.success("Domanda aggiornata");
      } else {
        const res = await api.post(`/courses/${courseId}/questions`, body);
        data = res.data;
        toast.success("Domanda creata");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="question-form-dialog" className="max-w-2xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="wordmark text-brand-blue">
            {isEdit ? "Modifica domanda" : "Nuova domanda"}
          </DialogTitle>
          <DialogDescription>
            Le opzioni devono essere esattamente 4. Contrassegna quella corretta.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Testo della domanda</Label>
            <textarea
              data-testid="qf-text"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              className="mt-1.5 w-full min-h-[80px] rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Opzioni di risposta</Label>
            {form.options.map((opt, i) => (
              <label key={i} className="flex items-center gap-2 border border-brand-line rounded-sm px-3 py-2 has-[input[type=radio]:checked]:border-brand-blue has-[input[type=radio]:checked]:bg-brand-blue/5">
                <input
                  type="radio"
                  name="correct"
                  data-testid={`qf-correct-${i}`}
                  checked={form.correct_index === i}
                  onChange={() => setForm({ ...form, correct_index: i })}
                  className="accent-brand-blue"
                />
                <span className="text-xs font-semibold w-5 text-brand-muted">{String.fromCharCode(65 + i)}.</span>
                <Input
                  data-testid={`qf-option-${i}`}
                  value={opt}
                  onChange={(e) => {
                    const next = [...form.options];
                    next[i] = e.target.value;
                    setForm({ ...form, options: next });
                  }}
                  className="h-9 rounded-sm flex-1 border-0 focus-visible:ring-0 shadow-none px-0"
                  placeholder={`Opzione ${i + 1}`}
                  required
                />
              </label>
            ))}
          </div>

          <div>
            <Label>Spiegazione (opzionale, mostrata dopo la risposta)</Label>
            <textarea
              data-testid="qf-explanation"
              value={form.explanation}
              onChange={(e) => setForm({ ...form, explanation: e.target.value })}
              className="mt-1.5 w-full min-h-[60px] rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>

          <div>
            <Label>Tag (separati da virgola, opzionale)</Label>
            <Input
              data-testid="qf-tags"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="mt-1.5 h-10 rounded-sm"
              placeholder="modulo1, normativa"
            />
          </div>

          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-brand-muted">Annulla</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="qf-submit"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? "Salva modifiche" : "Crea domanda"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
